import type { GetServerSideProps } from 'next'
import { fillPromotionResults, getProductBySku, getProductByUrl, getProductReviews } from '@api/products'
import { getParsedCustom8OrEmptyObject } from '@utils/product-data'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import type { Product } from '@model/product'
import { logError } from '@utils/errors-client'
import { SESSION_ID_COOKIE } from '@pages/api/auth'
import { getAllPromotions, getCurrencies } from '@api/misc'
import { getPrices } from '@utils/product/product-info'
import type { CurrenciesInfo } from '@hooks/useCurrency'
import { getCurrencyOptByLanguageId } from '@hooks/useCurrency'
import { propEq } from 'ramda'
import { unSlug } from '@utils/slugs'
import { getBestPrice } from '@api/ecw/price'
import { KK_STORE } from '@api/apiConfig'
import type { KK_STORE_TYPE } from '@pages/api/proxy'
import type { Catalog } from '@model/pricelist/Catalog'
import { isPromotionValidForCatalog } from '@utils/promotion'

export { ProductPage as default } from '@components/pages/ProductPage'

export const getServerSideProps: GetServerSideProps = async ({ params, locale, req }) => {
  const { slug, catalogId = '' } = params as { slug: string; catalogId: string }
  const languageId = KK_LANG_IDS[locale as Locale]
  const sessionId = req.cookies[SESSION_ID_COOKIE]
  const sku = unSlug(slug)

  try {
    const product: Product = await getProductBySku(sku, languageId, catalogId ? catalogId as Catalog : undefined)

    if (KK_STORE === 'store1' && product.url && catalogId) {
      const productByUrl = await getProductByUrl(product.url, languageId, catalogId as Catalog)
      product.price0 = productByUrl.price0 || 0
      product.price2 = productByUrl.price2 || 0
    }

    const promotions = await getAllPromotions(sessionId, languageId)
    const { promotionResults = [] } = await fillPromotionResults([product], languageId, promotions)

    const freeProductPromo = promotions.find(propEq('orderTotalCode', 'ot_free_product'))

    product.promotionResults = promotionResults.map((promo) => ({
      ...promo,
      ...(freeProductPromo && { promotionName: freeProductPromo.name, description: freeProductPromo.description }),
    }))

    if (!product) throw new Error(`Cannot get product by sku ${sku}`)

    const reviews = await getProductReviews(sku, languageId)
    const currencies: CurrenciesInfo[] = await getCurrencies(languageId)
    const currencyOpt = getCurrencyOptByLanguageId(languageId, currencies)

    try {
      const filteredPromotions = promotions.filter(isPromotionValidForCatalog((catalogId as Catalog) || ''))
      product.priceInfo = getPrices(
        product,
        KK_STORE as KK_STORE_TYPE,
        locale as string,
        filteredPromotions,
        currencyOpt,
        '1'
      )
      product.variantInfo = getParsedCustom8OrEmptyObject(product)
      product.productPromotion = filteredPromotions
      if (KK_STORE === 'store1' && product.priceInfo && product.priceInfo.bundleIncludedProducts?.length) {
        const productPromises = product.priceInfo.bundleIncludedProducts.map(
          (product): Promise<Product> => getProductByUrl(product.url, 1, catalogId as Catalog)
        )
        product.priceInfo.bundleIncludedProducts = await Promise.all(productPromises)
      }

      if (KK_STORE === 'store2') {
        product.ecwPrice = await getBestPrice(product.custom2Int, product.sku)
      }
    } catch (e) {
      logError(`Error on server side for ProductPage, sku=${sku}`, e, true)
    }

    return {
      props: {
        product,
        reviews,
        recommendations: [],
        recentlyViewedProducts: [],
      },
    }
  } catch (e) {
    logError(`Error on server side for ProductPage, sku=${sku}`, e, true)
    return {
      notFound: true,
    }
  }
}
